import { useMemo, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { adminMdaCodes, useAuth } from '../../lib/auth';
import type { Officer } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

export function Officers() {
  const { persona } = useAuth();
  const roles = persona?.roles ?? [];
  const isHr = roles.includes('HR_LIAISON');
  const myMdas = adminMdaCodes(roles);
  const canSee = isHr || myMdas.length > 0;

  const [mdaCode, setMdaCode] = useState('');
  const [q, setQ] = useState('');
  const [showOnboard, setShowOnboard] = useState(false);
  const [grantTarget, setGrantTarget] = useState<Officer | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const query = useMemo(() => {
    const p = new URLSearchParams({ limit: '100' });
    if (mdaCode) p.set('mdaCode', mdaCode);
    if (q) p.set('q', q);
    return `/api/v1/staffdesk/officers?${p}`;
  }, [mdaCode, q]);
  const listQ = useApiQuery<{ items: Officer[] }>(canSee ? query : null);

  const canActOn = (o: Officer) => isHr || myMdas.includes(o.mdaCode);

  const setStatus = async (o: Officer, status: Officer['status']) => {
    setActionError(null);
    try {
      await api.post(`/api/v1/staffdesk/officers/${o.id}/status`, { status });
      listQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Status update failed');
    }
  };

  if (!canSee) {
    return (
      <div>
        <PageHeader title="Officer registry" subtitle="Every officer onboarded to StaffDesk, by MDA" />
        <EmptyState title="No access" hint="This screen needs HR_LIAISON, or an MDA_ADMIN:<code> role for your own MDA." />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Officer registry"
        subtitle="Every officer onboarded to StaffDesk, by MDA"
        actions={
          isHr ? (
            <button className="btn-primary" onClick={() => setShowOnboard(true)}>
              + Onboard officer
            </button>
          ) : undefined
        }
      />
      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}
      <div className="mb-4 flex flex-wrap gap-2">
        <input className="field-input max-w-[220px]" placeholder="Search position or personId…" value={q} onChange={(e) => setQ(e.target.value)} />
        <input className="field-input max-w-[140px]" placeholder="MDA code" value={mdaCode} onChange={(e) => setMdaCode(e.target.value)} />
      </div>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No officers found" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Person ID</th>
                <th className="table-head">MDA</th>
                <th className="table-head">Position</th>
                <th className="table-head">Roles</th>
                <th className="table-head">Status</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((o) => (
                <tr key={o.id} className="hover:bg-slate-50">
                  <td className="table-cell font-mono text-xs">{o.personId}</td>
                  <td className="table-cell text-xs">{o.mdaCode}</td>
                  <td className="table-cell">{o.position}</td>
                  <td className="table-cell">
                    <div className="flex flex-wrap gap-1">
                      {o.roles.length === 0 ? (
                        <span className="text-xs text-slate-300">none</span>
                      ) : (
                        o.roles.map((r) => (
                          <span key={r} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500 ring-1 ring-slate-200">
                            {r}
                          </span>
                        ))
                      )}
                    </div>
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={o.status} />
                  </td>
                  <td className="table-cell text-right">
                    {canActOn(o) ? (
                      <div className="flex justify-end gap-1.5">
                        {isHr ? (
                          <button className="btn-secondary !px-2 !py-0.5 text-xs" onClick={() => setGrantTarget(o)}>
                            Propose role
                          </button>
                        ) : null}
                        {o.status === 'ACTIVE' ? (
                          <button className="btn-danger !px-2 !py-0.5 text-xs" onClick={() => setStatus(o, 'SUSPENDED')}>
                            Suspend
                          </button>
                        ) : o.status === 'SUSPENDED' ? (
                          <button className="btn-success !px-2 !py-0.5 text-xs" onClick={() => setStatus(o, 'ACTIVE')}>
                            Reactivate
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showOnboard ? (
        <OnboardModal
          onClose={() => setShowOnboard(false)}
          onDone={() => {
            setShowOnboard(false);
            listQ.reload();
          }}
        />
      ) : null}
      {grantTarget ? <ProposeRoleModal officer={grantTarget} onClose={() => setGrantTarget(null)} onDone={() => setGrantTarget(null)} /> : null}
    </div>
  );
}

function OnboardModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [personId, setPersonId] = useState('');
  const [mdaCode, setMdaCode] = useState('');
  const [position, setPosition] = useState('');
  const [roles, setRoles] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/staffdesk/officers', {
        personId,
        mdaCode,
        position,
        roles: roles
          .split(',')
          .map((r) => r.trim())
          .filter(Boolean),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Onboarding failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Onboard officer" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Person ID (SeyID / NIN)">
            <input className="field-input" value={personId} onChange={(e) => setPersonId(e.target.value)} placeholder="nin-850612-4471" />
          </Field>
          <Field label="MDA code">
            <input className="field-input" value={mdaCode} onChange={(e) => setMdaCode(e.target.value)} placeholder="SRC" />
          </Field>
        </div>
        <Field label="Position">
          <input className="field-input" value={position} onChange={(e) => setPosition(e.target.value)} placeholder="Tax Officer" />
        </Field>
        <Field label="Initial roles (comma-separated, optional)">
          <input className="field-input" value={roles} onChange={(e) => setRoles(e.target.value)} placeholder="CASE_OFFICER" />
        </Field>
        <p className="text-xs text-slate-400">Every role change after onboarding goes through the role-grant maker-checker queue.</p>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !personId || !mdaCode || !position}>
            {busy ? 'Onboarding…' : 'Onboard'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ProposeRoleModal({ officer, onClose, onDone }: { officer: Officer; onClose: () => void; onDone: () => void }) {
  const [role, setRole] = useState('');
  const [action, setAction] = useState<'GRANT' | 'REVOKE'>('GRANT');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/staffdesk/officers/${officer.id}/role-grants`, { role, action });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Proposal failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Propose a role change — ${officer.personId}`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <p className="text-xs text-slate-500">Current roles: {officer.roles.join(', ') || 'none'}</p>
        <Field label="Role code">
          <input className="field-input" value={role} onChange={(e) => setRole(e.target.value)} placeholder="CASE_OFFICER" />
        </Field>
        <Field label="Action">
          <select className="field-input" value={action} onChange={(e) => setAction(e.target.value as 'GRANT' | 'REVOKE')}>
            <option value="GRANT">Grant</option>
            <option value="REVOKE">Revoke</option>
          </select>
        </Field>
        <p className="text-xs text-slate-400">This goes to the officer's own MDA_ADMIN for approval — it does not take effect immediately.</p>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !role}>
            {busy ? 'Submitting…' : 'Submit for approval'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
